import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { BoardSeatRepresentingLabels } from '../../enums/board-seat-representing.enum';
import { BoardSeat, CreateBoardSeat, Pagination, Person } from '../../models';
import { SpinnerService } from '../../../xform-compat';
import { DeleteConfirmationComponent } from '../delete-confirmation/delete-confirmation.component';
import { CONFIG } from '../../../config';
import { BoardSeatService } from '../../../core/data-services/board-seat.service';
import { BoardSeatAddEditComponent } from '../board-seat-add-edit/board-seat-add-edit.component';
import { BoardSeatDataSource } from './BoardSeatDataSource';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateTime } from 'luxon';
import { RestrictionsIntegrationService } from '../../../modules/restrictions/restrictions-integration.service';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { PERMISSIONS } from '../../enums/permissions.enum';
import { skip } from 'rxjs/operators';

@Component({
    selector: 'rms-board-seats-table',
    templateUrl: './board-seats-table.component.html',
    styleUrls: ['./board-seats-table.component.scss'],
    standalone: false
})
export class BoardSeatsTableComponent implements OnInit {

  @ViewChild(BoardSeatAddEditComponent, { static: false }) boardSeatAddEditComponent: BoardSeatAddEditComponent;
  @ViewChild(DeleteConfirmationComponent, { static: false }) deleteConfirmationComponent: DeleteConfirmationComponent;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  /** The header row as a native `<tr>`, for the Restrictions page's pinned copy on phones (rmsPinnedTableStack). */
  @ViewChild('pinnedHeadRow', { static: true }) pinnedHeadRow: TemplateRef<unknown>;
  @ViewChild('updateFailConfirm', { static: false }) updateFailConfirm: ConfirmationModalComponent;

  private _filter: any = {};
  @Input() set filter(value: any) {
    this._filter = value;
    this.updateDisplayedColumns();
    if (this.dataSource) {
      this.pagination.page = 1;
      this.dataSource.setFilter(this._filter);
    }
  }

  private _tabLayout = false;
  /**
   * The company page's Restrictions/Board Seats tab: the columns that repeat the company come out, secondary
   * values move under the main ones, Person is plain text, and narrow tab columns get cards.
   */
  @Input() set tabLayout(value: boolean) {
    this._tabLayout = value;
    this.updateDisplayedColumns();
  }

  @Output() personClick: EventEmitter<Person> = new EventEmitter<Person>();
  /** A sort tapped in the pinned header copy, so the page can bring the re-sorted rows into view. */
  @Output() pinnedSort: EventEmitter<void> = new EventEmitter<void>();
  /** A save or delete from this table succeeded, so totals the page keeps elsewhere may have changed. */
  @Output() recordsChanged: EventEmitter<void> = new EventEmitter<void>();

  public dataSource: BoardSeatDataSource;
  public pagination: Pagination;
  public totalCount = 0;
  // The current page's records and whether the first page has arrived, for the tab's cards and heading
  // True while a page of records is loading, for the Restrictions page's phone drawer (VIEW RESULTS)
  public loading = false;
  public records: Array<BoardSeat> = [];
  public loaded = false;

  private readonly TAB_COLUMNS = ['person.lastName', 'boardRole', 'startDate', 'contact', 'companyPosition', 'lastModified'];
  // Set by updateDisplayedColumns from the layout and the filter's Status
  public displayedColumns: Array<string> = [];

  private readonly destroyRef = inject(DestroyRef);

  public readonly BoardSeatRepresentingLabels = BoardSeatRepresentingLabels;
  public readonly PERMISSIONS = PERMISSIONS;

  // Sort ids for the sortable columns, where they differ from the column name; unlisted columns don't sort
  private readonly SORT_IDS: Record<string, string> = {
    'company.name': 'company.name', 'representing': 'representing', 'person.lastName': 'person.lastName',
    'companyPosition': 'companyPosition', 'company.isPrivate': 'company.isPrivate', 'boardRole': 'boardRole',
    'startDate': 'startDate', 'endDate': 'endDate', 'resignationSentDate': 'resignationSentDate', 'lastModified': 'updated'
  };

  protected readonly DateTime = DateTime;
  protected readonly CONFIG = CONFIG;

  constructor(
    public spinnerService: SpinnerService,
    public integrationService: RestrictionsIntegrationService,
    private boardSeatService: BoardSeatService,
  ) {
    const defaultPagination = {
      ...CONFIG.pagination,
      sort: 'company.name',
      direction: 'asc'
    };
    this.pagination = new Pagination(defaultPagination);
    this.updateDisplayedColumns();
  }

  ngOnInit() {
    this.initDataSource();
    this.updateDisplayedColumns();
  }

  /**
   * The tab drops the columns that repeat the company. The Restrictions page shows the company, with its
   * ticker and Public/Private under it, and End once Inactive or All is selected.
   */
  private updateDisplayedColumns() {
    if (this._tabLayout) {
      this.displayedColumns = this.TAB_COLUMNS;
      return;
    }
    const includesEnded = this._filter?.active !== true;
    this.displayedColumns = [
      'company.name',
      'person.lastName',
      'boardRole',
      'startDate',
      ...(includesEnded ? ['endDate'] : []),
      'contact',
      'companyPosition',
      'lastModified'
    ];
  }


  public openBoardSeatAddEdit(boardSeat?: BoardSeat): void {
    if (this.spinnerService.active) {
      return;
    }
    const boardSeatAddEditRef = this.boardSeatAddEditComponent.openModal(boardSeat);
    boardSeatAddEditRef.result.then((result: CreateBoardSeat) => {
      if (result) {
        this.spinnerService.start();
        let operation;
        if (!boardSeat?.active && result?.endDate) {
          operation = this.boardSeatService.updateBoardSeatEndDate(boardSeat.id,
            { endDate: result.endDate.toUTC().toFormat(CONFIG.dateTimeServerFormatFullMillis) });
        } else if (boardSeat?.id) {
          operation = this.boardSeatService.updateBoardSeat(boardSeat.id, result);
        } else {
          operation =this.boardSeatService.createBoardSeat(result);
        }
        this.subscribeWithErrorHandling(operation);
      }
    });
  }

  public deleteBoardSeat(boardSeat: BoardSeat) {
    if (this.spinnerService.active) {
      return;
    }
    this.deleteConfirmationComponent.data = {
      startDate: boardSeat.startDate
    };
    const deleteConfirmationRef = this.deleteConfirmationComponent.openModal(false);
    deleteConfirmationRef.result.then((result: { reason: string, endDate: DateTime, forceEzeError: boolean }) => {
      if (result) {
        const { endDate, forceEzeError } = result;
        this.spinnerService.start();
        this.subscribeWithErrorHandling(
          this.boardSeatService.deleteBoardSeat(boardSeat.id, endDate, forceEzeError)
        );
      }
    });
  }

  /** The company's sub-line on the Restrictions page: "ASND · Public", or "Private". */
  public getCompanyLine(boardSeat: BoardSeat): string {
    if (boardSeat.company?.isPrivate) {
      return 'Private';
    }
    return boardSeat.company?.ticker ? `${boardSeat.company.ticker} · Public` : 'Public';
  }

  public canEdit(): boolean {
    return !!this.integrationService.hasRestrictionPermission[PERMISSIONS.BoardRestrictionUpdate];
  }

  public canDelete(boardSeat: BoardSeat): boolean {
    return boardSeat.active && !!this.integrationService.hasRestrictionPermission[PERMISSIONS.BoardRestrictionDelete];
  }

  /** Whether the row's kebab would have any items, so an empty menu is never offered. */
  public hasRowActions(boardSeat: BoardSeat): boolean {
    return this.canEdit() || this.canDelete(boardSeat);
  }

  /** Header label for a column; shared by the real header row and the pinned copy. */
  public columnLabel(column: string): string {
    const labels: Record<string, string> = {
      'company.name': 'Company', 'company.ticker': 'Ticker', 'representing': 'Representing', 'person.lastName': 'Person',
      'companyPosition': 'Int. Position', 'company.isPrivate': 'Cur. Position', 'boardRole': 'Role', 'committees': 'Committee(s)',
      'startDate': this._tabLayout ? 'Since' : 'Start', 'endDate': 'End', 'resignationSentDate': 'Resg. Sent', 'contact': 'Contact',
      'lastModified': 'Last Modified'
    };
    return labels[column] ?? '';
  }

  /** The CDK's own class for a column's cells, so the pinned copy's cells can be styled alike. */
  public columnClass(column: string): string {
    return `mat-column-${column.replace(/[^a-z0-9_-]/gi, '-')}`;
  }

  public isSortable(column: string): boolean {
    return !!this.SORT_IDS[column];
  }

  public isSortedBy(column: string): boolean {
    return !!this.sort && this.sort.active === this.SORT_IDS[column];
  }

  /** Company ascending; any other sort fills its header darker. */
  get isDefaultSort(): boolean {
    return this.sort?.active === 'company.name' && this.sort?.direction === 'asc';
  }

  /** Sorts from a tap in the pinned header copy, as a tap on the real header would. */
  public sortFromPin(column: string) {
    const id = this.SORT_IDS[column];
    if (!id) {
      return;
    }
    this.sort.sort({ id, start: 'asc', disableClear: false });
    this.pinnedSort.emit();
  }

  /** No pager when every row fits on one page. */
  get showPager(): boolean {
    return this.totalCount > this.pagination.pagesize;
  }

  get tabLayout(): boolean {
    return this._tabLayout;
  }

  public onPageChange() {
    if (this.dataSource) {
      this.dataSource.setPagination(this.pagination);
    }
  }

  private initDataSource() {
    this.dataSource = new BoardSeatDataSource(this.boardSeatService, this.sort, this.integrationService, this.pagination);
    this.dataSource.setFilter(this.filter);
    this.dataSource.totalCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(total => {
        this.totalCount = total;
      });
    this.dataSource.records$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(records => {
        this.records = records;
        this.loaded = true;
      });
    this.dataSource.isLoading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(resp => {
        // Wrap in setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.loading = resp;
          if (resp) {
            this.spinnerService.start();
          } else {
            this.spinnerService.stop();
          }
        }, 0);
      })
  }

  get filter() {
    return this._filter;
  }

  /**
   * Subscribes to an observable with standardized error handling and completion logic.
   * Stops the spinner and refreshes the data source on completion.
   * Displays error modal on error.
   */
  private subscribeWithErrorHandling(operation: any) {
    operation.subscribe(
      () => {},
      (error: string) => {
        this.handleSaveError(error);
      },
      () => {
        this.spinnerService.stop();
        this.dataSource.refresh();
        this.recordsChanged.emit();
      }
    );
  }

  private handleSaveError(errorMsg: string) {
    this.updateFailConfirm.body = errorMsg;
    this.updateFailConfirm.headerText = null;
    this.updateFailConfirm.cancelText = null;
    this.updateFailConfirm.confirmText = 'Okay';
    this.updateFailConfirm.openModal();
  }
}
